import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createManagedAudio } from "utils/audioMixer";
import { AUTOMATION_TIMELINE } from "../../landingContent";
import SectionHeading from "../SectionHeading";
import PresentScreenMockFrame from "./PresentScreenMockFrame";

const getActiveEventIndex = (events, currentTime) => {
  let activeIndex = 0;

  events.forEach((event, index) => {
    if (currentTime >= event.videoTime) {
      activeIndex = index;
    }
  });

  return activeIndex;
};

const TIMELINE_TIME_WITH_SECONDS_PATTERN = /^(\d{1,2}:\d{2})(:\d{2})(\s*(?:AM|PM))?$/i;

const TimelineTimeDisplay = ({ time }) => {
  const match = time.match(TIMELINE_TIME_WITH_SECONDS_PATTERN);

  if (!match) {
    return time;
  }

  const [, hourMinute, seconds, meridiem = ""] = match;

  return (
    <>
      {hourMinute}
      <span className="landing-v2-timeline__time-seconds">{seconds}</span>
      {meridiem}
    </>
  );
};

const MOCK_AUTO_ADVANCE_DELAYS_MS = {
  0: 11000,
  1: 16000,
  2: 15000,
  3: 9000,
  4: 9000,
  5: 16000,
  6: 13000,
  7: 10000,
};

// Mobile renders the right-side timeline mock as the measured desktop canvas,
// scaled down as a single unit so its internals never reflow.
const TIMELINE_MOCK_DESKTOP_WIDTH = 670.375;
const TIMELINE_MOCK_DESKTOP_HEIGHT = 376.203;
const TIMELINE_MOCK_MOBILE_QUERY = "(max-width: 767px)";
const MOBILE_HIDDEN_FOOTER_ITEM = "No forgotten timers.";

const getIsTimelineMockScaledViewport = () => (
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(TIMELINE_MOCK_MOBILE_QUERY).matches
);

const stopAudioPreview = (audio, onStopped) => {
  if (!audio) {
    onStopped?.();
    return;
  }

  audio.pause();
  audio.currentTime = 0;
  onStopped?.();
};

const AutomationTimelineSection = ({
  embedded = false,
  showHeading = true,
  headline = AUTOMATION_TIMELINE.headline,
  supportingCopy = AUTOMATION_TIMELINE.supportingCopy,
}) => {
  const { events, videoPlaceholder, videoSrc } = AUTOMATION_TIMELINE;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);
  const [isMockFastForwarding, setIsMockFastForwarding] = useState(false);
  const [isMockAudioPlaying, setIsMockAudioPlaying] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isTimelineMockScaled, setIsTimelineMockScaled] = useState(getIsTimelineMockScaledViewport);
  const [timelineMockScale, setTimelineMockScale] = useState(null);
  const audioPreviewRef = useRef(null);
  const beatRefs = useRef([]);
  const timelineMockScaleRef = useRef(null);
  const timelineListRef = useRef(null);
  const videoRef = useRef(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;

    if (!section) {
      return undefined;
    }

    if (!("IntersectionObserver" in window)) {
      setIsInView(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.2, rootMargin: "80px 0px" },
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const timelineList = timelineListRef.current;
    const visibleBeat = beatRefs.current[activeIndex];

    if (!timelineList || !visibleBeat) {
      return;
    }

    const isVerticalTimeline = window.matchMedia("(min-width: 900px)").matches;
    const scrollOptions = isVerticalTimeline
      ? {
        top: visibleBeat.offsetTop - timelineList.offsetTop - ((timelineList.clientHeight - visibleBeat.clientHeight) / 2),
        behavior: "smooth",
      }
      : {
        left: visibleBeat.offsetLeft - timelineList.offsetLeft - ((timelineList.clientWidth - visibleBeat.clientWidth) / 2),
        behavior: "smooth",
      };

    timelineList.scrollTo(scrollOptions);
  }, [activeIndex]);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(TIMELINE_MOCK_MOBILE_QUERY);
    const handleChange = (event) => setIsTimelineMockScaled(event.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  useLayoutEffect(() => {
    const node = timelineMockScaleRef.current;

    if (!isTimelineMockScaled || !node) {
      return undefined;
    }

    const updateScale = () => {
      if (node.clientWidth > 0 && node.clientHeight > 0) {
        setTimelineMockScale(Math.min(
          node.clientWidth / TIMELINE_MOCK_DESKTOP_WIDTH,
          node.clientHeight / TIMELINE_MOCK_DESKTOP_HEIGHT
        ));
      }
    };

    updateScale();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateScale, { passive: true });
      return () => window.removeEventListener("resize", updateScale);
    }

    const observer = new ResizeObserver(updateScale);
    observer.observe(node);
    return () => observer.disconnect();
  }, [isTimelineMockScaled]);

  useEffect(() => {
    if (videoSrc || !isInView) {
      return undefined;
    }

    const autoAdvanceDelay = MOCK_AUTO_ADVANCE_DELAYS_MS[activeIndex];
    const nextIndex = activeIndex + 1 >= events.length ? 0 : activeIndex + 1;

    if (!autoAdvanceDelay) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setActiveIndex(nextIndex);
    }, autoAdvanceDelay);

    return () => window.clearTimeout(timeoutId);
  }, [activeIndex, events.length, isInView, videoSrc]);

  useEffect(() => {
    if (!isInView || !isAudioEnabled) {
      stopAudioPreview(audioPreviewRef.current, () => setIsMockAudioPlaying(false));
      return undefined;
    }

    const activeEvent = events[activeIndex];
    playEventAudioPreview(activeEvent?.audioSrc, activeEvent?.audioCategory);
  }, [activeIndex, events, isAudioEnabled, isInView]);

  useEffect(() => () => {
    stopAudioPreview(audioPreviewRef.current, () => setIsMockAudioPlaying(false));
    audioPreviewRef.current = null;
  }, []);

  const playEventAudioPreview = (audioSrc, audioCategory = "music") => {
    stopAudioPreview(audioPreviewRef.current, () => setIsMockAudioPlaying(false));
    audioPreviewRef.current = null;

    if (!isAudioEnabled || !audioSrc) {
      setIsMockAudioPlaying(false);
      return;
    }

    const audio = createManagedAudio(audioSrc, {
      category: audioCategory,
      fallback: 0.8,
    });

    if (!audio) {
      setIsMockAudioPlaying(false);
      return;
    }

    const clearPlayingState = () => {
      if (audioPreviewRef.current === audio) {
        audioPreviewRef.current = null;
        setIsMockAudioPlaying(false);
      }
    };

    audio.preload = "auto";
    audio.onended = clearPlayingState;

    audioPreviewRef.current = audio;
    audio.play?.().then(() => {
      if (audioPreviewRef.current === audio) {
        setIsMockAudioPlaying(true);
      }
    }).catch(clearPlayingState);
  };

  const handleAudioToggle = () => {
    setIsAudioEnabled((enabled) => {
      const nextEnabled = !enabled;

      if (!nextEnabled) {
        stopAudioPreview(audioPreviewRef.current, () => setIsMockAudioPlaying(false));
        audioPreviewRef.current = null;
      }

      return nextEnabled;
    });
  };

  const handleTimelineClick = (index) => {
    const video = videoRef.current;
    const selectedEvent = events[index];
    const targetTime = selectedEvent.videoTime;

    setActiveIndex(index);

    if (isAudioEnabled && index === activeIndex) {
      playEventAudioPreview(selectedEvent.audioSrc, selectedEvent.audioCategory);
    }

    if (video && Number.isFinite(targetTime)) {
      video.currentTime = targetTime;
      video.play?.().catch(() => {
        // Autoplay may be blocked until the visitor interacts with the video controls.
      });
    }
  };

  const handleVideoTimeUpdate = () => {
    const video = videoRef.current;

    if (!video) {
      return;
    }

    setActiveIndex(getActiveEventIndex(events, video.currentTime));
  };

  const timelineContent = (
    <>
      {showHeading ? (
        <SectionHeading
          headline={headline}
          subheading={supportingCopy}
          headingLevel={2}
        />
      ) : null}

      <div
        className="landing-v2-timeline landing-v2-timeline--interactive"
      >
        <div className="landing-v2-timeline__video-panel">
          <div className="landing-v2-timeline__audio-control-row">
            <button
              type="button"
              className={`landing-v2-timeline__audio-toggle${
                isAudioEnabled ? " landing-v2-timeline__audio-toggle--on" : ""
              }`}
              aria-pressed={isAudioEnabled}
              onClick={handleAudioToggle}
            >
              <span className="landing-v2-timeline__audio-toggle-icon" aria-hidden="true">
                <i className={`fa ${isAudioEnabled ? "fa-volume-up" : "fa-volume-off"}`} />
              </span>
              <span className="landing-v2-timeline__audio-toggle-copy">
                <span className="landing-v2-timeline__audio-toggle-label">
                  Sound {isAudioEnabled ? "on" : "off"}
                </span>
              </span>
            </button>
          </div>
          {videoSrc ? (
            <video
              ref={videoRef}
              className="landing-v2-timeline__video"
              src={videoSrc}
              controls
              muted={!isAudioEnabled}
              playsInline
              preload="metadata"
              poster={AUTOMATION_TIMELINE.videoPoster}
              onTimeUpdate={handleVideoTimeUpdate}
              onLoadedMetadata={handleVideoTimeUpdate}
            />
          ) : (
            <div
              className={`landing-v2-timeline__video-placeholder landing-v2-timeline__video-placeholder--present-mock${
                isTimelineMockScaled ? " landing-v2-timeline__video-placeholder--scaled-mock" : ""
              }`}
              role="img"
              aria-label="Animated Lesson Launchpad Present Screen automation mockup"
            >
              <div ref={timelineMockScaleRef} className="landing-v2-timeline__mock-scale">
                <div
                  className={`landing-v2-timeline__mock-canvas${
                    isTimelineMockScaled ? " landing-v2-timeline__mock-canvas--scaled" : ""
                  }`}
                  style={isTimelineMockScaled && timelineMockScale ? {
                    "--timeline-mock-scale": String(timelineMockScale),
                  } : undefined}
                >
                  <PresentScreenMockFrame
                    variant="timeline"
                    activeAutomationIndex={activeIndex}
                    ariaLabel={videoPlaceholder}
                    onFastForwardChange={setIsMockFastForwarding}
                    isMockAudioPlaying={isMockAudioPlaying}
                    isAnimationPaused={!isInView}
                    heroWidgetLayout
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <ol
          ref={timelineListRef}
          className="landing-v2-timeline__list"
          aria-label="Automation demo timeline"
          tabIndex={0}
        >
          {events.map(({ id, time, label, description }, index) => {
            const isActive = index === activeIndex;
            const isCompleted = index < activeIndex;

            return (
              <li
                key={id}
                ref={(element) => {
                  beatRefs.current[index] = element;
                }}
                className={`landing-v2-timeline__item${
                  isActive ? " landing-v2-timeline__item--active" : ""
                }${
                  isCompleted ? " landing-v2-timeline__item--completed" : ""
                }`}
              >
                <div className="landing-v2-timeline__marker" aria-hidden="true">
                  <span className="landing-v2-timeline__dot" />
                  {index < events.length - 1 ? <span className="landing-v2-timeline__line" /> : null}
                </div>
                <button
                  type="button"
                  className="landing-v2-timeline__button"
                  onClick={() => handleTimelineClick(index)}
                  aria-current={isActive ? "step" : undefined}
                >
                  {isCompleted ? (
                    <span className="landing-v2-timeline__completed-pill">
                      Complete
                    </span>
                  ) : null}
                  {isActive && isMockAudioPlaying ? (
                    <span
                      className={`landing-v2-timeline__audio-playing${
                        isMockFastForwarding ? " landing-v2-timeline__audio-playing--with-fast-forward" : ""
                      }`}
                      aria-hidden="true"
                    >
                      <i className="fa fa-volume-up" />
                    </span>
                  ) : null}
                  {isActive && isMockFastForwarding ? (
                    <span className="landing-v2-timeline__fast-forward" aria-hidden="true">
                      <span />
                      <span />
                    </span>
                  ) : null}
                  <span className="landing-v2-timeline__time">
                    <TimelineTimeDisplay time={time} />
                  </span>
                  <span className="landing-v2-timeline__label">{label}</span>
                  <span className="landing-v2-timeline__description">{description}</span>
                </button>
              </li>
            );
          })}
        </ol>
        <ul className="landing-v2-timeline__footer-checklist landing-v2-timeline__footer-line">
          {AUTOMATION_TIMELINE.footerLineItems.map((item) => (
            <li
              key={item}
              className={
                item === MOBILE_HIDDEN_FOOTER_ITEM
                  ? "landing-v2-timeline__footer-item--mobile-hidden"
                  : undefined
              }
            >
              <span className="landing-v2-timeline__footer-check" aria-hidden="true">
                <i className="fa fa-check" />
              </span>
              {item}
            </li>
          ))}
        </ul>
      </div>
    </>
  );

  if (embedded) {
    return (
      <div ref={sectionRef} className="landing-v2-automation-timeline landing-v2-automation-timeline--embedded">
        {timelineContent}
      </div>
    );
  }

  return (
    <section
      ref={sectionRef}
      className="landing-v2__section landing-v2-automation-timeline"
      aria-labelledby="timeline-heading"
    >
      <div className="landing-v2__container">
        {timelineContent}
      </div>
    </section>
  );
};

export default AutomationTimelineSection;
